// ============================================================
// نقشه‌ی تعاملی نمایش موقعیت
//
// جایگزین NeshanInteractiveMap. آن کامپوننت چون فکر می‌کرد کاشی از
// داخل ایران در دسترس نیست، روی یک *تصویر استاتیک* نشان، جابه‌جایی
// و زوم را با دست پیاده کرده بود: تبدیل‌های Web Mercator، درگ با
// pointer event، محاسبه‌ی جای پین. همه‌ی آن حالا کار خود Leaflet است.
//
// رفتار بیرونی عوض نشده: همان props و همان کنترل‌ها.
// ============================================================

import React, { useEffect, useState } from "react";
import { MapContainer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, ImageOff, MapPin, Minus, Moon, Plus, Sun } from "lucide-react";

import MapAutoSize from "./MapAutoSize";
import TileLayerWithFallback from "./TileLayerWithFallback";
import { MAX_ZOOM, MIN_ZOOM, pulsePin, useIsDarkMode } from "./tiles";

interface InteractiveMapProps {
    lat: number;
    lng: number;
    height?: number;
    initialZoom?: number;
    onZoomChange?: (zoom: number) => void;
}

/** پل بین state ری‌اکت و نمونه‌ی Leaflet: جابه‌جایی، زوم و خبر دادن زوم. */
const MapBridge: React.FC<{
    lat: number;
    lng: number;
    zoom: number;
    recenterKey: number;
    onZoomChange?: (zoom: number) => void;
}> = ({ lat, lng, zoom, recenterKey, onZoomChange }) => {
    const map = useMap();

    useEffect(() => {
        map.setView([lat, lng], zoom);
        // zoom عمداً در وابستگی‌ها نیست: این افکت فقط برای جابه‌جایی
        // است. recenterKey با هر کلیک دکمه‌ی «بازگشت به موقعیت» عوض
        // می‌شود تا حتی با مختصات یکسان هم دوباره اجرا شود.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, lat, lng, recenterKey]);

    // زوم جدا اعمال می‌شود، وگرنه دکمه‌های +/− کاری نمی‌کردند. شرط
    // getZoom حلقه را می‌بندد: zoomend خودش zoom را به بالا خبر می‌دهد.
    useEffect(() => {
        if (map.getZoom() !== zoom) map.setZoom(zoom);
    }, [map, zoom]);

    useEffect(() => {
        if (!onZoomChange) return;
        const report = () => onZoomChange(map.getZoom());
        report();
        map.on("zoomend", report);
        return () => {
            map.off("zoomend", report);
        };
    }, [map, onZoomChange]);

    return null;
};

const InteractiveMap: React.FC<InteractiveMapProps> = ({
    lat,
    lng,
    height = 340,
    initialZoom = 17,
    onZoomChange,
}) => {
    const systemDark = useIsDarkMode();
    // کاربر می‌تواند نقشه را مستقل از حالت سامانه تیره/روشن کند.
    const [darkOverride, setDarkOverride] = useState<boolean | null>(null);
    const dark = darkOverride === null ? systemDark : darkOverride;

    const [failed, setFailed] = useState(false);
    const [recenterKey, setRecenterKey] = useState(0);
    const [zoom, setZoom] = useState(initialZoom);
    const [mapKey, setMapKey] = useState(0);

    useEffect(() => {
        setFailed(false);
        setZoom(initialZoom);
    }, [lat, lng, initialZoom]);

    const reportZoom = React.useCallback(
        (value: number) => {
            setZoom(value);
            onZoomChange?.(value);
        },
        [onZoomChange]
    );

    const btn =
        "flex items-center justify-center transition bg-white/95 dark:bg-zink-700/95 text-slate-700 dark:text-zink-100 size-8 rounded-lg shadow-md hover:bg-white dark:hover:bg-zink-600 disabled:opacity-40 disabled:cursor-not-allowed";

    if (failed) {
        return (
            <div
                className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-100 dark:bg-zink-600 text-slate-400"
                style={{ height }}
            >
                <ImageOff size={38} className="opacity-50" />
                <p className="text-sm">نمایش نقشه ممکن نشد.</p>
                <button
                    type="button"
                    onClick={() => {
                        setFailed(false);
                        setMapKey((k) => k + 1);
                    }}
                    className="px-3 py-1.5 text-xs border rounded-lg border-slate-300 hover:bg-slate-100 dark:hover:bg-zink-500"
                >
                    تلاش مجدد
                </button>
            </div>
        );
    }

    return (
        <div
            className="relative overflow-hidden rounded-xl bg-slate-100 dark:bg-zink-600"
            style={{ height }}
        >
            <MapContainer
                key={mapKey}
                center={[lat, lng]}
                zoom={initialZoom}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                zoomControl={false}
                scrollWheelZoom
                // ردگیری resize به MapAutoSize سپرده شده — توضیحش آنجاست
                trackResize={false}
                style={{ height: "100%", width: "100%", zIndex: 1 }}
            >
                <TileLayerWithFallback dark={dark} onExhausted={() => setFailed(true)} />
                <Marker position={[lat, lng]} icon={pulsePin()} />
                <MapAutoSize />
                <MapBridge
                    lat={lat}
                    lng={lng}
                    zoom={zoom}
                    recenterKey={recenterKey}
                    onZoomChange={reportZoom}
                />
            </MapContainer>

            {/* ===== کنترل‌ها ===== */}
            <div className="absolute z-[2] flex flex-col gap-1.5 top-3 right-3">
                <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 1))}
                    disabled={zoom >= MAX_ZOOM}
                    className={btn}
                    title="بزرگ‌نمایی"
                >
                    <Plus size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 1))}
                    disabled={zoom <= MIN_ZOOM}
                    className={btn}
                    title="کوچک‌نمایی"
                >
                    <Minus size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setZoom(initialZoom);
                        setRecenterKey((k) => k + 1);
                    }}
                    className={btn}
                    title="بازگشت به موقعیت"
                >
                    <Crosshair size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => setDarkOverride(!dark)}
                    className={btn}
                    title={dark ? "نقشه روشن" : "نقشه تیره"}
                >
                    {dark ? <Sun size={16} /> : <Moon size={16} />}
                </button>
            </div>

            {/* ===== نوار پایین ===== */}
            <div className="absolute z-[2] flex items-center gap-2 px-2.5 py-1 text-[11px] rounded-lg shadow bottom-3 right-3 bg-white/90 dark:bg-zink-700/90 text-slate-600 dark:text-zink-100">
                <MapPin size={12} className="text-red-500" />
                <span>بزرگ‌نمایی: {zoom}</span>
                <span className="text-slate-300 dark:text-zink-400">|</span>
                <span>برای جابه‌جایی، نقشه را بکشید</span>
            </div>
        </div>
    );
};

export default InteractiveMap;
